import * as THREE from 'three';

// portalCapture — photograph the EXACT patch of world a placement quad covers.
//
// This is the load-bearing piece of the portal concept. Happy Oyster's Directing
// mode uses the supplied image directly as frame 0, so if the capture is framed
// exactly to the quad — same centre, same orientation, same width and height —
// then the video's opening frame is pixel-aligned with the wall it's about to
// replace. Swap the quad's material to the stream and the wall doesn't switch
// on like a screen; it comes alive and opens. Any framing error (a perspective
// capture, a crop, a different aspect) shows up as a visible seam or a jump at
// the moment the video starts, which is exactly what kills the illusion.
//
// Hence ORTHOGRAPHIC: a perspective camera would apply its own projection to
// the wall's plane and the composite would only line up dead-centre. An ortho
// camera sized to the quad maps the quad's rectangle 1:1 to the image.

// 16:9 — Happy Oyster rejects any first frame outside 1.5–2.0 landscape, so the
// capture is always this regardless of the placement's own aspect.
const CAP_W = 1280;
const CAP_H = 720;

/**
 * A frame of nothing but the key colour, for CHROMA portals.
 *
 * Directing mode uses the supplied image directly as frame 0, so handing it a
 * flat green screen is the strongest available way to say "this is what the
 * background is" — the video literally starts on the colour we intend to key,
 * instead of the model inventing an opening frame and drifting from there.
 * Same 16:9 as the wall capture, so it clears the 1.5–2.0 landscape rule.
 *
 * @param {string} hex e.g. '#00b140'
 * @returns {string} data:image/jpeg URL
 */
export function flatFrame(hex) {
  const canvas = document.createElement('canvas');
  canvas.width = CAP_W;
  canvas.height = CAP_H;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = hex;
  ctx.fillRect(0, 0, CAP_W, CAP_H);
  // Quality 1.0: JPEG ringing around a flat fill is invisible to the eye but
  // shifts pixels off the key colour, which is exactly what a keyer trips on.
  return canvas.toDataURL('image/jpeg', 1.0);
}

/**
 * Render the wall behind a portal quad into a JPEG data URL.
 *
 * @param {object}  o
 * @param {THREE.WebGLRenderer} o.gl
 * @param {THREE.Scene}  o.scene
 * @param {THREE.Vector3} o.position  quad centre, world space
 * @param {THREE.Quaternion} o.quaternion  quad orientation (its +Z is the wall normal)
 * @param {number}  o.width   quad width in metres
 * @param {number}  o.height  quad height in metres
 * @param {THREE.Object3D} [o.hide]  the portal's own group — hidden for the shot
 *                                   so we photograph the WALL, not our placeholder
 * @returns {string|null} data:image/jpeg URL, or null if the read failed
 */
export function captureSurface({ gl, scene, position, quaternion, width, height, hide }) {
  // Frame to the quad's own rectangle, then widen (never crop) to 16:9 — a crop
  // would lose wall the video is about to cover, and the seam would show.
  const aspect = width / Math.max(1e-6, height);
  const frameW = aspect >= CAP_W / CAP_H ? width : height * (CAP_W / CAP_H);
  const frameH = aspect >= CAP_W / CAP_H ? width / (CAP_W / CAP_H) : height;

  const cam = new THREE.OrthographicCamera(-frameW / 2, frameW / 2, frameH / 2, -frameH / 2, 0.01, 400);
  cam.quaternion.copy(quaternion);
  // Stand OFF the wall along the quad's normal and look back at it. The near
  // plane then sits in front of the camera, so anything between the viewer and
  // the wall (props, NPCs, the crosshair ghost) is excluded from frame 0.
  const normal = new THREE.Vector3(0, 0, 1).applyQuaternion(quaternion).normalize();
  cam.position.copy(position).addScaledVector(normal, 6);
  cam.lookAt(position);
  cam.updateProjectionMatrix();
  cam.updateMatrixWorld();

  const target = new THREE.WebGLRenderTarget(CAP_W, CAP_H, {
    colorSpace: THREE.SRGBColorSpace,
    depthBuffer: true,
  });

  const prevTarget = gl.getRenderTarget();
  const wasVisible = hide ? hide.visible : null;
  if (hide) hide.visible = false;

  let dataUrl = null;
  try {
    gl.setRenderTarget(target);
    gl.render(scene, cam);
    const pixels = new Uint8Array(CAP_W * CAP_H * 4);
    gl.readRenderTargetPixels(target, 0, 0, CAP_W, CAP_H, pixels);

    // GL reads bottom-up; canvas is top-down. Flip row-wise or frame 0 arrives
    // upside down — and an upside-down first frame is a world built upside down.
    const canvas = document.createElement('canvas');
    canvas.width = CAP_W;
    canvas.height = CAP_H;
    const ctx = canvas.getContext('2d');
    const img = ctx.createImageData(CAP_W, CAP_H);
    const rowBytes = CAP_W * 4;
    for (let y = 0; y < CAP_H; y++) {
      const src = (CAP_H - 1 - y) * rowBytes;
      img.data.set(pixels.subarray(src, src + rowBytes), y * rowBytes);
    }
    ctx.putImageData(img, 0, 0);
    // 0.85 keeps a 1280×720 frame around 150–250 KB — comfortably under the
    // gateway's 2 MB cap even before it goes to GCS as a URL instead.
    dataUrl = canvas.toDataURL('image/jpeg', 0.85);
  } catch (e) {
    console.warn('[portal] capture failed:', e);
  } finally {
    gl.setRenderTarget(prevTarget);
    if (hide) hide.visible = wasVisible;
    target.dispose();
  }
  return dataUrl;
}
